import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { blockMutations, SUPPORTED_API_MINOR } from '../api/client';
import { useHealth } from '../api/queries';

const icons: Record<string, ReactNode> = {
  home: <path d="M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" />,
  radiator: <><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M7.5 6v12M12 6v12M16.5 6v12M6 18v2M18 18v2" /></>,
  routine: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></>,
  wake: <><circle cx="12" cy="13" r="7" /><path d="M12 10v3l2 2M5 4L3 6M19 4l2 2" /></>,
  measure: <path d="M14 14.76V4a2 2 0 0 0-4 0v10.76a4 4 0 1 0 4 0z" />,
  presence: <><circle cx="12" cy="10" r="3" /><path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z" /></>,
  settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></>,
  more: <><circle cx="5" cy="12" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="19" cy="12" r="1.5" /></>,
};

export function Icon({ name, size = 22 }: { name: keyof typeof icons; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {icons[name]}
    </svg>
  );
}

const NAV = [
  { to: '/', label: 'Tableau de bord', short: 'Accueil', icon: 'home', mobile: true },
  { to: '/radiateurs', label: 'Radiateurs & pièces', short: 'Radiateurs', icon: 'radiator', mobile: true },
  { to: '/evenements', label: 'Réveil, forçages, absences', short: 'Réveil', icon: 'wake', mobile: true },
  { to: '/routines', label: 'Routines', short: 'Routines', icon: 'routine', mobile: true },
  { to: '/mesure', label: 'Mesure de déperdition', short: 'Mesure', icon: 'measure', mobile: false },
  { to: '/presence', label: 'Présence & occupation', short: 'Présence', icon: 'presence', mobile: false },
  { to: '/reglages', label: 'Réglages & santé', short: 'Réglages', icon: 'settings', mobile: false },
] as const;

function VersionBanner() {
  const health = useHealth();
  const v = health.data?.api_version;
  const unsupported = !!v && !v.startsWith(`${SUPPORTED_API_MINOR}.`);
  useEffect(() => {
    blockMutations(unsupported ? `API ${v} : ce front attend ${SUPPORTED_API_MINOR}.x` : null);
  }, [unsupported, v]);
  if (health.error) {
    return <div className="banner banner-bad" role="alert">Service injoignable. Les données affichées peuvent être anciennes.</div>;
  }
  if (unsupported) {
    return (
      <div className="banner banner-bad" role="alert">
        L’API répond en version {v}, ce front est écrit pour {SUPPORTED_API_MINOR}.x : les modifications sont désactivées.
      </div>
    );
  }
  if (health.data?.status === 'degraded') {
    return <div className="banner banner-warn" role="status">Service dégradé : base {health.data.database}, MQTT {health.data.mqtt}, moteur {health.data.engine}.</div>;
  }
  return null;
}

export function Layout() {
  const [moreOpen, setMoreOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setMoreOpen(false), [loc.pathname]);
  const health = useHealth();

  return (
    <div className="shell">
      <a href="#main" className="skip">Aller au contenu</a>
      <nav className="sidebar" aria-label="Navigation principale">
        <div className="brand">
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="#F08A4B" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><rect x="3" y="7" width="22" height="15" rx="3" /><path d="M8 7v15M12.5 7v15M17 7v15M21 7v15M7 22v3M21 22v3" /></svg>
          <div>
            <div className="brand-name">Smart Energy Control</div>
            <div className="brand-sub">API {health.data?.api_version ?? '…'}</div>
          </div>
        </div>
        <div className="nav-list">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === '/'} className="nav-item">
              <Icon name={n.icon} size={18} />
              {n.label}
            </NavLink>
          ))}
        </div>
      </nav>

      <div className="content">
        <VersionBanner />
        <main id="main" className="main">
          <Outlet />
        </main>
      </div>

      <nav className="tabbar" aria-label="Navigation">
        {NAV.filter((n) => n.mobile).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === '/'} className="tab">
            <Icon name={n.icon} />
            {n.short}
          </NavLink>
        ))}
        <button type="button" className={`tab ${moreOpen ? 'active' : ''}`} aria-expanded={moreOpen} aria-controls="more-menu" onClick={() => setMoreOpen((o) => !o)}>
          <Icon name="more" />
          Plus
        </button>
        {moreOpen && (
          <div id="more-menu" className="more-menu">
            {NAV.filter((n) => !n.mobile).map((n) => (
              <NavLink key={n.to} to={n.to} className="more-item">
                <Icon name={n.icon} size={20} />
                {n.label}
              </NavLink>
            ))}
          </div>
        )}
      </nav>
    </div>
  );
}
